import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import { inviaProforma } from '../../api/fatture';

interface Props {
  bozzaId: string;
  emailDefault?: string;
  ragioneSociale: string;
  onClose: () => void;
  onSent: () => void;
}

const inputCls =
  'w-full text-sm border border-stone-border rounded px-3 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';
const labelCls = 'block text-xs font-medium text-ink-soft mb-1';

/** Invio del PDF pro forma via email (POST /fatture/bozza/{id}/proforma/invia). */
export default function ProformaInviaModal({ bozzaId, emailDefault = '', ragioneSociale, onClose, onSent }: Props) {
  const { t } = useTranslation();
  const [emailTo, setEmailTo] = useState(emailDefault);
  const [emailCc, setEmailCc] = useState('');
  const [subject, setSubject] = useState(t('fatturazione.bozza.proforma_subject_default', { cliente: ragioneSociale }));
  const [body, setBody] = useState(t('fatturazione.bozza.proforma_body_default'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleInvia() {
    setError(null);
    if (!emailTo.trim()) { setError(t('fatturazione.bozza.val_destinatario')); return; }
    setLoading(true);
    try {
      const cc = emailCc.split(',').map((s) => s.trim()).filter(Boolean);
      await inviaProforma(bozzaId, {
        email_to: emailTo.trim(),
        cc: cc.length ? cc : null,
        subject: subject.trim() || null,
        body,
      });
      onSent();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('fatturazione.bozza.errore_invio_proforma'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      title={t('fatturazione.bozza.invia_proforma')}
      subtitle={ragioneSociale}
      onClose={onClose}
      testid="proforma-invia-modal"
      footer={(
        <>
          <button type="button" onClick={onClose} disabled={loading}
            className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50">
            {t('common.azioni.annulla')}
          </button>
          <button type="button" onClick={handleInvia} disabled={loading} data-testid="btn-conferma-invia-proforma"
            className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60">
            {loading ? t('fatturazione.bozza.invio_in_corso') : t('fatturazione.bozza.invia')}
          </button>
        </>
      )}
    >
      <div>
        <label className={labelCls}>{t('fatturazione.bozza.destinatario')} <span className="text-accent-danger">*</span></label>
        <input className={inputCls} type="email" value={emailTo} data-testid="proforma-email-to"
          onChange={(e) => setEmailTo(e.target.value)} />
      </div>
      <div>
        <label className={labelCls}>{t('fatturazione.bozza.cc')}</label>
        <input className={inputCls} type="text" value={emailCc}
          onChange={(e) => setEmailCc(e.target.value)} placeholder="email1@example.com, email2@example.com" />
      </div>
      <div>
        <label className={labelCls}>{t('fatturazione.bozza.oggetto')}</label>
        <input className={inputCls} type="text" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </div>
      <div>
        <label className={labelCls}>{t('fatturazione.bozza.corpo_email')}</label>
        <textarea className={inputCls} rows={5} value={body} onChange={(e) => setBody(e.target.value)} />
      </div>
      {error && (
        <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger" data-testid="proforma-invia-error">
          {error}
        </div>
      )}
    </Modal>
  );
}
